import { useState, useEffect } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import api from '../../lib/axios'
import toast from 'react-hot-toast'
import clsx from 'clsx'

const CATEGORY_META = {
  new_features: { label: 'New Features',    color: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20', icon: '✨' },
  improvements: { label: 'Improvements',    color: 'text-blue-400 bg-blue-500/10 border-blue-500/20',         icon: '⚡' },
  bug_fixes:    { label: 'Bug Fixes',       color: 'text-amber-400 bg-amber-500/10 border-amber-500/20',      icon: '🐛' },
  breaking:     { label: 'Breaking Changes',color: 'text-red-400 bg-red-500/10 border-red-500/20',            icon: '⚠️' },
  db_changes:   { label: 'DB Changes',      color: 'text-purple-400 bg-purple-500/10 border-purple-500/20',   icon: '🗄' },
}

export default function Changelog() {
  const { repositoryId } = useParams()
  const [searchParams]   = useSearchParams()
  const repoName         = searchParams.get('repo') ?? 'Repository'

  const [entries, setEntries]   = useState([])
  const [tags, setTags]         = useState([])
  const [loading, setLoading]   = useState(true)
  const [syncing, setSyncing]   = useState(false)
  const [expanded, setExpanded] = useState({})

  useEffect(() => {
    if (!repositoryId) return
    Promise.all([
      api.get(`/repositories/${repositoryId}/changelog`),
      api.get(`/repositories/${repositoryId}/release-tags`),
    ]).then(([changelogRes, tagsRes]) => {
      setEntries(changelogRes.data.data ?? changelogRes.data)
      setTags(tagsRes.data)
    }).finally(() => setLoading(false))
  }, [repositoryId])

  const syncTags = async () => {
    setSyncing(true)
    try {
      const { data } = await api.post(`/repositories/${repositoryId}/release-tags/sync`)
      toast.success(`Synced ${data.synced} release tags.`)
      const { data: fresh } = await api.get(`/repositories/${repositoryId}/release-tags`)
      setTags(fresh)
    } catch { toast.error('Sync failed.') }
    finally { setSyncing(false) }
  }

  const tagForEntry = (entry) => tags.find(t => t.changelog_entry_id === entry.id)

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-8 h-8 border-2 border-purple-500/30 border-t-purple-500 rounded-full animate-spin" />
    </div>
  )

  return (
    <div className="max-w-3xl mx-auto px-6 py-8">

      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display font-bold text-white text-2xl">Changelog</h1>
          <p className="text-sm text-white/40 mt-1">{repoName}</p>
        </div>
        <button onClick={syncTags} disabled={syncing}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/6 border border-white/10 text-white/50 hover:text-white hover:bg-white/10 text-xs font-semibold transition-all disabled:opacity-50">
          {syncing ? <><div className="w-3 h-3 border border-white/30 border-t-white rounded-full animate-spin" />Syncing…</> : <>🏷 Sync Release Tags</>}
        </button>
      </div>

      {entries.length === 0 ? (
        <div className="text-center py-20 text-white/30">
          <p className="text-4xl mb-4">📋</p>
          <p className="text-sm">No changelog entries yet.</p>
          <p className="text-xs mt-2 text-white/20">Entries are created automatically after each push analysis.</p>
        </div>
      ) : (
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-[19px] top-0 bottom-0 w-px bg-white/8" />

          <div className="space-y-6">
            {entries.map((entry, idx) => {
              const tag    = tagForEntry(entry)
              const cats   = entry.categories ?? {}
              const isOpen = expanded[entry.id] !== false
              const hasItems = Object.values(cats).some(v => Array.isArray(v) ? v.length > 0 : false)

              return (
                <div key={entry.id} className="relative flex gap-4 pl-10">
                  {/* Timeline dot */}
                  <div className={clsx(
                    'absolute left-0 top-1.5 w-10 h-10 flex items-center justify-center',
                  )}>
                    <div className={clsx(
                      'w-4 h-4 rounded-full border-2 flex-shrink-0',
                      tag ? 'bg-amber-500 border-amber-400' :
                      entry.has_breaking_changes ? 'bg-red-500 border-red-400' :
                      idx === 0 ? 'bg-purple-500 border-purple-400' :
                      'bg-white/20 border-white/30'
                    )} />
                  </div>

                  {/* Entry card */}
                  <div className="flex-1 min-w-0 glass-card rounded-xl overflow-hidden">
                    <button type="button" onClick={() => setExpanded(p => ({ ...p, [entry.id]: !isOpen }))}
                      className="w-full text-left p-4 hover:bg-white/3 transition-colors">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            {/* Release tag badge */}
                            {tag && (
                              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/25">
                                🏷 {tag.tag_name}
                              </span>
                            )}
                            {entry.has_breaking_changes && (
                              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-500/15 text-red-400 border border-red-500/25">
                                ⚠️ Breaking
                              </span>
                            )}
                            <code className="text-[10px] text-purple-400 font-mono bg-purple-500/10 px-1.5 py-0.5 rounded">
                              {entry.commit_sha?.slice(0, 7)}
                            </code>
                            <span className="text-[10px] text-white/30 font-mono">{entry.branch}</span>
                          </div>
                          <p className="text-sm font-semibold text-white truncate">{entry.title}</p>
                          <p className="text-xs text-white/40 mt-0.5 line-clamp-1">{entry.summary}</p>
                        </div>

                        <div className="flex-shrink-0 text-right">
                          <p className="text-[11px] text-white/30">
                            {new Date(entry.committed_at ?? entry.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </p>
                          {entry.files_changed > 0 && (
                            <p className="text-[10px] text-white/20 mt-0.5">{entry.files_changed} files</p>
                          )}
                          <svg className={clsx('w-3.5 h-3.5 text-white/20 mt-1 ml-auto transition-transform', !isOpen && '-rotate-90')}
                            fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/>
                          </svg>
                        </div>
                      </div>
                    </button>

                    {/* Expanded detail */}
                    {isOpen && hasItems && (
                      <div className="px-4 pb-4 border-t border-white/6 pt-3 space-y-3">
                        {Object.entries(cats).map(([key, items]) => {
                          if (!Array.isArray(items) || items.length === 0) return null
                          const meta = CATEGORY_META[key]
                          if (!meta) return null
                          return (
                            <div key={key}>
                              <p className={clsx('text-[9px] font-bold uppercase tracking-widest mb-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full border', meta.color)}>
                                {meta.icon} {meta.label}
                              </p>
                              <ul className="space-y-1.5 ml-2">
                                {items.map((item, i) => (
                                  <li key={i} className="flex items-start gap-2 text-xs text-white/60">
                                    <span className="text-white/20 mt-0.5">·</span>
                                    <span>{typeof item === 'string' ? item : item.title ?? JSON.stringify(item)}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
